import { useState, useEffect, useRef } from "react";

import store from "../store/index";

import {
  Check,
  X,
  MoreVertical,
  Plus,
  ChevronDown,
  Search,
  PrinterIcon,
} from "lucide-react";

import { SUCCESS } from "../assets/constants";
import { usePrinters } from "../hooks/usePrinters";
import { useCloud } from "../hooks/useCloud";

import Loader from "../components/Loader";
import Switch from "../components/Switch";
import AddPrinter from "../components/AddPrinter";
import Staff from "../components/Settings/Staff";
import PrinterDeviceCard from "../components/PrinterDeviceCard";

import toast from "react-hot-toast";
import DownloadCenter from "../components/Settings/DownloadCenter";
import { observer } from "mobx-react-lite";

const ENABLED_SETTINGS = [
  "partners",
  "gst",
  "fssai",
  "staff",
  "printer",
  "download",
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState("gst");

  // GST CONFIGURATIONS
  const { gstUpdate, updateGST } = useCloud();
  const [state, setState] = useState({
    gstActive: false,
    selectedValue: "",
    gstn: "",
    sgst: "",
    cgst: "",
    acSgst: "",
    acCgst: "",
    vat: "",
    serviceCharge: "",
  });
  const stateRef = useRef(state);
  const [editOption, setEditOption] = useState(false);

  useEffect(() => {
    const { GST } = store;
    const isGST = GST?.active || false;
    const selectedValue = GST?.gstType || "";
    const gstn = GST?.gstNumber || "";
    const sgst = GST?.rates.SGST || 0;
    const cgst = GST?.rates.CGST || 0;
    const acSgst = GST?.rates.ACSGST || 0;
    const acCgst = GST?.rates.ACCGST || 0;
    const vat = GST?.vat || 0;
    const serviceCharge = GST?.serviceTaxRate || 0;
    const fassi_no = GST?.fassi_no || 0;
    setState((prev) => ({
      selectedValue,
      gstn,
      sgst,
      cgst,
      acSgst,
      acCgst,
      vat,
      gstActive: isGST,
      serviceCharge,
      isGST,
      fassi_no,
    }));
    stateRef.current = {
      selectedValue,
      gstn,
      sgst,
      cgst,
      acSgst,
      acCgst,
      vat,
      gstActive: isGST,
      serviceCharge,
      isGST,
      fassi_no,
    };
  }, []);

  const handleStateChange = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
    stateRef.current = { ...stateRef.current, [key]: value };
  };

  const handleSave = async () => {
    const {
      gstn,
      selectedValue,
      sgst,
      cgst,
      acSgst,
      acCgst,
      vat,
      serviceCharge,
      fassi_no,
      gstActive,
    } = stateRef.current;

    const payload = {
      gstNumber: gstn,
      isGst: gstActive,
      fassiNo: fassi_no,
      vat: vat,
      gstType: selectedValue,
      serviceTaxEnabled: serviceCharge ? true : false,
      serviceTaxRate: serviceCharge || "0",
      gstRate: {
        CGST: cgst,
        SGST: sgst,
        ACCGST: acCgst,
        ACSGST: acSgst,
      },
    };

    const { status } = await updateGST(payload);
    if (status === SUCCESS) {
      store.GST = {
        rates: {
          CGST: cgst,
          SGST: sgst,
          ACCGST: acCgst,
          ACSGST: acSgst,
        },
        gstType: selectedValue,
        gstNumber: gstn,
        active: gstActive,
        fassi_no: fassi_no,
        vat: vat,
        serviceCharge: serviceCharge,
        serviceTaxEnabled: serviceCharge ? true : false,
        serviceTaxRate: serviceCharge || "0",
      };
      toast.success("GST and tax details saved successfully!");
    } else {
      toast.error("GST and tax details failed to save! please try again");
    }
  };

  const toggleGST = (value) => {
    const updatedGstActive = value;
    localStorage.setItem("isGST", updatedGstActive);

    stateRef.current = { ...state, gstActive: updatedGstActive };
    setState(stateRef.current);
    handleSave();
  };

  // PARTNER CONFIGURATIONS
  const [partners, setPartners] = useState([]);

  // State for form
  const [selectedPartner, setSelectedPartner] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingPartnerId, setEditingPartnerId] = useState(null);

  // State for dropdown menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = useRef(null);

  useEffect(() => {
    const storedPartners =
      JSON.parse(localStorage.getItem("integratedPartners")) || [];
    setPartners(Array.isArray(storedPartners) ? storedPartners : []);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // if (menuRef.current && !menuRef.current.contains(event.target)) {
      //   setOpenMenuId(null)
      // }
      if (
        partnerDropdownRef.current &&
        !partnerDropdownRef.current.contains(event.target)
      ) {
        setIsPartnerDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle form submission
  const handleSubmit = () => {
    if (!selectedPartner || !restaurantId) return;

    if (editingPartnerId) {
      // Update existing partner
      setPartners(
        partners.map((partner) =>
          partner.id === editingPartnerId
            ? { ...partner, partner: selectedPartner, id: restaurantId }
            : partner
        )
      );
      setEditingPartnerId(null);
    } else {
      // Add new partner
      const newPartner = {
        id: Date.now().toString(),
        partner: selectedPartner,
      };
      setPartners([...partners, newPartner]);
      localStorage.setItem(
        "integratedPartners",
        JSON.stringify([...partners, newPartner])
      );
    }

    // Reset form
    setSelectedPartner("");
    setRestaurantId("");
    setIsAddingNew(false);
  };

  // Start editing a partner
  const handleEdit = (partner) => {
    setSelectedPartner(partner.partner);
    setRestaurantId(partner.id);
    setEditingPartnerId(partner.id);
    setIsAddingNew(true);
    setOpenMenuId(null);
  };

  // Delete a partner
  const handleDelete = (id) => {
    setPartners(partners.filter((partner) => partner.id !== id));
    setOpenMenuId(null);
    localStorage.setItem("integratedPartners", JSON.stringify(partners));
  };

  // Cancel adding/editing
  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingPartnerId(null);
    setSelectedPartner("");
    setRestaurantId("");
  };

  // Select partner from dropdown
  const handleSelectPartner = (partner) => {
    setSelectedPartner(partner);
    setIsPartnerDropdownOpen(false);
  };

  return (
    <div className="bg-gray-50 ">
      <div className=" h-screen mx-auto p-4 box-border bg-gray-50 overflow-x-hidden relative hide-scrollbar">
        <div className="bg-white rounded-lg shadow-lg border border-gray-300 h-full overflow-x-scroll hide-scrollbar relative ">
          <div className="p-2 px-6 bg-white border-b border-gray-300 rounded-t-lg  bg-white flex-wrap gap-2 sticky top-0 flex flex-col gap-4 rounded-t-lg bg-white">
            <div>
              <h2 className="font-semibold text-lg text-gray-900">Settings</h2>
            </div>
          </div>

          {/* Tabs */}
          <div
            className="border-b bg-white  border-gray-300 sticky top-0"
            style={{ zIndex: 49 }}
          >
            <div className="flex overflow-x-auto">
              {ENABLED_SETTINGS.includes("gst") && (
                <button
                  onClick={() => setActiveTab("gst")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "gst"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  GST/VAT
                </button>
              )}
              {ENABLED_SETTINGS.includes("fssai") && (
                <button
                  onClick={() => setActiveTab("fssai")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "fssai"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  FSSAI
                </button>
              )}
              {ENABLED_SETTINGS.includes("partners") && (
                <button
                  onClick={() => setActiveTab("partners")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "partners"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Partners
                </button>
              )}
              {ENABLED_SETTINGS.includes("staff") && (
                <button
                  onClick={() => setActiveTab("staff")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "staff"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Staff
                </button>
              )}
              {ENABLED_SETTINGS.includes("notifications") && (
                <button
                  onClick={() => setActiveTab("notifications")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "notifications"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Notifications
                </button>
              )}
              {ENABLED_SETTINGS.includes("printer") && (
                <button
                  onClick={() => setActiveTab("printer")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "printer"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Printers
                </button>
              )}
              {ENABLED_SETTINGS.includes("download") && (
                <button
                  onClick={() => setActiveTab("download")}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === "download"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : " hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Downloads
                </button>
              )}
            </div>
          </div>

          {/* Partners Tab Content */}
          {activeTab === "partners" && <Partner_Management />}

          {/* GST Tab Content */}
          {activeTab === "gst" && (
            <div className="p-6">
              {!state.gstActive ? (
                <div className="">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">
                      GST Status
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="md:col-span-1">
                      <h3 className="text-base font-medium text-gray-900">
                        Activate GST
                      </h3>
                    </div>
                    <div className="md:col-span-3">
                      <div className="space-y-4">
                        <div>
                          <Switch
                            value={state.gstActive}
                            onChange={toggleGST}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-800">
                        GST & Tax Configuration
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div className="md:col-span-1">
                        <h3 className="text-base font-medium text-gray-900">
                          Pricing Configuration
                        </h3>
                      </div>
                      <div className="md:col-span-3">
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Select Pricing Type
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              value={state.selectedValue}
                              onChange={(e) =>
                                handleStateChange(
                                  "selectedValue",
                                  e.target.value
                                )
                              }
                            >
                              <option>Select Pricing Type</option>
                              <option value="Include">Inclusive of Tax</option>
                              <option value="Exclude">Exclusive of Tax</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-300 pt-6 mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div className="md:col-span-1">
                        <h3 className="text-base font-medium text-gray-900">
                          GSTN Details
                        </h3>
                      </div>
                      <div className="md:col-span-3">
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              GSTN Number
                            </label>
                            <input
                              type="text"
                              placeholder="Enter your GSTN"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              value={state.gstn}
                              onChange={(e) =>
                                handleStateChange("gstn", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-300 pt-6 mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div className="md:col-span-1">
                        <h3 className="text-base font-medium text-gray-900">
                          Tax Rates
                        </h3>
                      </div>
                      <div className="md:col-span-3">
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                SGST (%)
                              </label>
                              <input
                                type="text"
                                placeholder="Enter SGST"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={state.sgst}
                                onChange={(e) =>
                                  handleStateChange("sgst", e.target.value)
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                CGST (%)
                              </label>
                              <input
                                type="text"
                                placeholder="Enter CGST"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={state.cgst}
                                onChange={(e) =>
                                  handleStateChange("cgst", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                AC SGST (%)
                              </label>
                              <input
                                type="text"
                                placeholder="Enter AC SGST"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={state.acSgst}
                                onChange={(e) =>
                                  handleStateChange("acSgst", e.target.value)
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                AC CGST (%)
                              </label>
                              <input
                                type="text"
                                placeholder="Enter AC CGST"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={state.acCgst}
                                onChange={(e) =>
                                  handleStateChange("acCgst", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              VAT (%)
                            </label>
                            <input
                              type="text"
                              placeholder="Enter VAT"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              value={state.vat}
                              onChange={(e) =>
                                handleStateChange("vat", e.target.value)
                              }
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Service Charge (%)
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Service Charge"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              value={state.serviceCharge}
                              onChange={(e) =>
                                handleStateChange(
                                  "serviceCharge",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 text-right flex gap-2">
                    <div className="flex-1"> </div>
                    <button
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors"
                      onClick={handleSave}
                    >
                      Save Tax Settings
                    </button>
                    <button
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors"
                      onClick={() => toggleGST(false)}
                    >
                      Deactivate GST
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* FSSAI Tab Content */}
          {activeTab === "fssai" && (
            <div className="p-6">
              <>
                <div className="">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">
                      FSSAI
                    </h2>
                  </div>
                </div>
                <div className="border-t border-gray-300 pt-6 mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="md:col-span-1">
                      <h3 className="text-base font-medium text-gray-900">
                        FSSAI Details
                      </h3>
                    </div>
                    <div className="md:col-span-3">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            FSSAI Number
                          </label>
                          <input
                            type="text"
                            placeholder="Enter your FSSAI"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={state.fassi_no}
                            onChange={(e) =>
                              handleStateChange("fassi_no", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-right flex gap-2">
                  <div className="flex-1"> </div>
                  <button
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors"
                    onClick={handleSave}
                  >
                    Save FSSAI
                  </button>
                </div>
              </>
            </div>
          )}

          {/* Staff Tab Content */}
          {activeTab === "staff" && <Staff />}

          {/* Notifications Tab Content */}
          {activeTab === "notifications" && (
            <div className="p-6">
              <div className="mb-8">
                <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors">
                  Test Digital order notification
                </button>
              </div>

              <div className="">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-1">
                    <h3 className="text-base font-medium text-gray-900">
                      Notification Channels
                    </h3>
                  </div>
                  <div className="md:col-span-3">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md">
                        <div>
                          <h3 className="font-medium text-gray-800">
                            Email Notifications
                          </h3>
                          <p className="text-sm ">
                            Receive order updates via email
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            defaultChecked
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md">
                        <div>
                          <h3 className="font-medium text-gray-800">
                            SMS Notifications
                          </h3>
                          <p className="text-sm ">
                            Receive order updates via SMS
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md">
                        <div>
                          <h3 className="font-medium text-gray-800">
                            Push Notifications
                          </h3>
                          <p className="text-sm ">
                            Receive push notifications for new orders
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            defaultChecked
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-300 pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-1">
                    <h3 className="text-base font-medium text-gray-900">
                      Contact Information
                    </h3>
                  </div>
                  <div className="md:col-span-3">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notification Email
                        </label>
                        <input
                          type="email"
                          placeholder="Enter email address"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notification Phone Number
                        </label>
                        <input
                          type="tel"
                          placeholder="Enter phone number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-right">
                <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors">
                  Save Notification Settings
                </button>
              </div>
            </div>
          )}

          {/* Printer Tab Content */}
          {activeTab === "printer" && <Printer_Management />}

          {/* Download Center Tab Content */}
          {activeTab === "download" && <DownloadCenter />}
        </div>
      </div>
    </div>
  );
}

function Partner_Management() {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [partnerEnabled, setPartnerEnabled] = useState(true);
  const [partners, setPartners] = useState([]);
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { updatePartners } = useCloud();

  const partnerDropdownRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    // Initial data is already in the correct format from store
    setPartners(store.restaurantInfo?.partnerDetails || []);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        partnerDropdownRef.current &&
        !partnerDropdownRef.current.contains(event.target)
      ) {
        setIsPartnerDropdownOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectPartner = (partner) => {
    setSelectedPartner(partner);
    setIsPartnerDropdownOpen(false);
  };

  const handleUpdatePartners = async (updatedPartners) => {
    setIsLoading(true);
    try {
      const response = await updatePartners({
        partnerDetails: updatedPartners,
      });

      if (response.status === SUCCESS) {
        setPartners(updatedPartners);
        store.restaurantInfo.partnerDetails = updatedPartners;
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating partners:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPartner || !partnerId) return;

    const updatedPartners = [...partners];
    const newPartner = {
      partnerId: partnerId,
      partnerName: selectedPartner,
      enabled: partnerEnabled,
    };

    if (editingPartnerId) {
      const index = updatedPartners.findIndex(
        (p) => p.partnerId === editingPartnerId
      );
      if (index !== -1) {
        updatedPartners[index] = newPartner;
      }
    } else {
      updatedPartners.push(newPartner);
    }

    const success = await handleUpdatePartners(updatedPartners);
    if (success) {
      toast.success(
        editingPartnerId
          ? "Partner updated successfully"
          : "Partner added successfully"
      );
      handleCancel();
    } else {
      toast.error(
        editingPartnerId ? "Failed to update partner" : "Failed to add partner"
      );
    }
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingPartnerId(null);
    setSelectedPartner("");
    setPartnerId("");
    setPartnerEnabled(true);
    setPartnerDropdownRef(null);
  };

  const handleEdit = (partner) => {
    setSelectedPartner(partner.partnerName);
    setPartnerId(partner.partnerId);
    setPartnerEnabled(partner.enabled);
    setEditingPartnerId(partner.partnerId);
    setIsAddingNew(true);
    setOpenMenuId(null);
  };

  const handleDelete = async (partnerId) => {
    const updatedPartners = partners.filter((p) => p.partnerId !== partnerId);
    const success = await handleUpdatePartners(updatedPartners);

    if (success) {
      toast.success("Partner deleted successfully");
    } else {
      toast.error("Failed to delete partner");
    }
    setOpenMenuId(null);
  };

  const handleTogglePartner = async (partnerId, currentStatus) => {
    const updatedPartners = partners.map((partner) =>
      partner.partnerId === partnerId
        ? { ...partner, enabled: !currentStatus }
        : partner
    );

    const success = await handleUpdatePartners(updatedPartners);
    if (success) {
      toast.success(
        `Partner ${!currentStatus ? "enabled" : "disabled"} successfully`
      );
    } else {
      toast.error("Failed to update partner status");
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-end mb-2">
        {!isAddingNew && (
          <button
            onClick={() => setIsAddingNew(true)}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-md border border-dashed border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={14} />
            Add New Partner
          </button>
        )}
      </div>

      <div className="border border-gray-300 rounded-md shadow-sm">
        <table className="w-full h-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-300">
                Partner
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-300">
                Partner ID
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-300">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Form row for adding/editing - always at the top */}
            {isAddingNew && (
              <tr className="bg-gray-50">
                <td className="px-4 py-3">
                  <div className="relative" ref={partnerDropdownRef}>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() =>
                        setIsPartnerDropdownOpen(!isPartnerDropdownOpen)
                      }
                      className="w-full flex items-center justify-between px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selectedPartner || "Partner"}
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${
                          isPartnerDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isPartnerDropdownOpen && (
                      <div className="absolute left-0 mt-1 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1">
                          {["Swiggy", "Zomato"].map((partner) => (
                            <button
                              key={partner}
                              onClick={() => handleSelectPartner(partner)}
                              className={`w-full text-left px-4 py-2 text-sm ${
                                selectedPartner === partner
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {partner}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={partnerId}
                    onChange={(e) => setPartnerId(e.target.value)}
                    placeholder="Enter partner ID"
                    disabled={isLoading}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <Switch
                    value={partnerEnabled}
                    onChange={() => setPartnerEnabled(!partnerEnabled)}
                    disabled={isLoading}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedPartner || !partnerId || isLoading}
                      className={`p-1.5 rounded-md ${
                        !selectedPartner || !partnerId || isLoading
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-gray-500 text-white hover:bg-gray-600"
                      }`}
                      title={editingPartnerId ? "Update" : "Save"}
                    >
                      {isLoading ? (
                        <span className="loading loading-spinner loading-sm"></span>
                      ) : (
                        <Check size={16} />
                      )}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="p-1.5 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Existing partners */}
            {partners.map((partner) => (
              <tr
                key={partner.partnerId}
                className={
                  editingPartnerId === partner.partnerId ? "hidden" : ""
                }
              >
                <td className="px-4 py-3 text-sm text-gray-900">
                  {partner.partnerName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {partner.partnerId}
                </td>
                <td className="px-4 py-3 text-center">
                  <Switch
                    value={partner.enabled}
                    onChange={() =>
                      handleTogglePartner(partner.partnerId, partner.enabled)
                    }
                    disabled={isLoading}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-block relative">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() =>
                        setOpenMenuId(
                          openMenuId === partner.partnerId
                            ? null
                            : partner.partnerId
                        )
                      }
                      className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MoreVertical size={16} />
                    </button>

                    {openMenuId === partner.partnerId && (
                      <div
                        className="absolute right-0 mt-1 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                        key={partner.partnerId}
                      >
                        <div className="py-1">
                          <button
                            onClick={() => handleEdit(partner)}
                            disabled={isLoading}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(partner.partnerId)}
                            disabled={isLoading}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {/* Empty state */}
            {partners.length === 0 && !isAddingNew && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-4 text-center text-sm text-gray-500"
                >
                  No partners integrated yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Printer_Management = observer(() => {
  const { printers, loading, addPrinter, searchPrinters } = usePrinters();

  const [search, setSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [filteredPrinters, setFilteredPrinters] = useState([]);

  const handleAddPrinter = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSavePrinter = async (printerData) => {
    const success = await addPrinter(printerData);
    if (success) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    if (search) {
      const printers = searchPrinters(search);
      setFilteredPrinters(printers);
    }
  }, [search]);

  useEffect(() => {
    setFilteredPrinters(printers);
  }, [printers, printers.length]);

  return (
    <div className="p-6">
      <div className="flex justify-end items-center mb-6 gap-2">
        <div className="relative w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search printers..."
          />
        </div>
        <button
          onClick={handleAddPrinter}
          className="flex items-center min-w-[140px] gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-md border border-dashed border-gray-300"
        >
          <Plus size={16} />
          Add Printer
        </button>
      </div>

      <AddPrinter
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSavePrinter}
      />

      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : (
        <div
          key={forceUpdate}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
        >
          {filteredPrinters.map((printer) => (
            <PrinterDeviceCard key={printer.id} printer={printer} />
          ))}

          {filteredPrinters.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12">
              <PrinterIcon size={100} className="text-gray-200 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-1">
                No Printers Configured
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Add your first printer to start managing your printing
                operations
              </p>
              <button
                onClick={handleAddPrinter}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md border border-dashed border-blue-300"
              >
                <Plus size={16} />
                Add Your First Printer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
